import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  InfoIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { TovButton } from '../TovButton/TovButton';
import { msg } from '../../localization/i18n';

export type ToastVariant = 'info' | 'success' | 'warning' | 'destructive';

const toastIcons: Record<ToastVariant, ReactNode> = {
  info: <InfoIcon aria-hidden="true" />,
  success: <CheckCircle2Icon aria-hidden="true" />,
  warning: <AlertTriangleIcon aria-hidden="true" />,
  destructive: <XCircleIcon aria-hidden="true" />,
};

export interface ToastRecord {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  variant: ToastVariant;
  durationMs: number;
  icon?: ReactNode;
  avatar?: ReactNode;
  action?: ReactNode;
  closeable: boolean;
}

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  durationMs?: number;
  icon?: ReactNode;
  avatar?: ReactNode;
  action?: ReactNode;
  closeable?: boolean;
}

interface ToastContextValue {
  toast(input: ToastInput): void;
  success(title: ReactNode, description?: ReactNode): void;
  warning(title: ReactNode, description?: ReactNode): void;
  destructive(title: ReactNode, description?: ReactNode): void;
  error(title: ReactNode, description?: ReactNode): void;
  info(title: ReactNode, description?: ReactNode): void;
  dismiss(id: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss(id: string): void;
}) {
  return (
    <div className="tov-toast-viewport" aria-atomic="true" aria-live="polite">
      {toasts.map((toast) => (
        <TovToast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export function TovToast({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss(id: string): void;
}) {
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.durationMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [onDismiss, toast.durationMs, toast.id]);

  return (
    <section
      className={cn('tov-toast', `tov-toast--soft-${toast.variant}`)}
      role={toast.variant === 'destructive' ? 'alert' : 'status'}
    >
      {toast.avatar ? (
        <div className="tov-toast__avatar">{toast.avatar}</div>
      ) : (
        <div className="tov-toast__icon">
          {toast.icon ?? toastIcons[toast.variant]}
        </div>
      )}
      <div className="tov-toast__content">
        <div className="tov-toast__body">
          <strong className="tov-toast__title">{toast.title}</strong>
          {toast.description ? (
            <p className="tov-toast__description">{toast.description}</p>
          ) : null}
        </div>
        {toast.action ? (
          <div className="tov-toast__action">{toast.action}</div>
        ) : null}
      </div>
      {toast.closeable ? (
        <TovButton
          aria-label={msg(
            'components.toast.components_toast_dismiss_notification',
          )}
          className="tov-toast__dismiss"
          onClick={() => onDismiss(toast.id)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon aria-hidden="true" />
        </TovButton>
      ) : null}
    </section>
  );
}

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback((input: ToastInput) => {
    setToasts((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        title: input.title,
        description: input.description,
        variant: input.variant ?? 'info',
        durationMs: input.durationMs ?? 5000,
        icon: input.icon,
        avatar: input.avatar,
        action: input.action,
        closeable: input.closeable ?? true,
      },
    ]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success(title, description) {
        toast({ title, description, variant: 'success' });
      },
      error(title, description) {
        toast({ title, description, variant: 'destructive', durationMs: 7000 });
      },
      info(title, description) {
        toast({ title, description, variant: 'info' });
      },
      warning(title, description) {
        toast({ title, description, variant: 'warning' });
      },
      destructive(title, description) {
        toast({ title, description, variant: 'destructive', durationMs: 7000 });
      },
      dismiss,
    }),
    [dismiss, toast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport onDismiss={dismiss} toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider.');
  }

  return context;
}
